import deepmerge from 'deepmerge';
import type { Config, DeepPartial } from '../default/types';
import defaultConfig, { getFullLocaleFromConfig, environment, customArrayMerge } from '../default';
import { productConfig } from './product/product';
import { installmentCalculatorConfig } from './product/installmentCalculator';
import { dynamicHelp } from './cms/dynamicHelp';
import { orderConfig } from './order/order';
import { paymentMethodsConfig } from './payment';

const tenantId = 'universalat';

const config = deepmerge<Config, DeepPartial<Config>>(
  defaultConfig,
  {
    host: { name: tenantId, domain: 'universal.at', appDeeplinkProtocol: 'uvsapp' },
    clientId: 'UniversalAt',
    i18n: {
      defaultLocale: 'de-AT',
      locales: ['de-AT'],
      languages: ['de'],
    },
    tenantId,
    company: {
      name: 'Universal',
      seoTitleName: 'Universal.at',
    },
    timeZone: 'Europe/Vienna',
    headerSlots: {
      joe: true,
      serviceApi: `https://storage.googleapis.com/drupal-pages/${environment}/menus/${tenantId}/<locale>/bucket-service-flyout.json`,
    },
    assets: {
      cmsImage: {
        imageOptimizationEnabled: true,
        uriTransformEndpoint: `https://storage.googleapis.com/drupal-images-${tenantId}/$1/`,
      },
      assetServerHost: 'https://bilder.universal.at',
    },
    staticContent: {
      apiEndpoints: {
        webappJson: `http://cloud-service-cms.${tenantId}.svc.cluster.local:3100`,
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    search: {
      adServer: {
        mode: 'containerOnly',
      },
    },
    middleware: {
      cmsRewrites: {
        exact: ['/', '/storefront', '/mafo/unsubscribe', '/uwg7/unsubscribe'],
        prefixes: [
          '/service-hilfe',
          '/lp',
          '/content',
          '/magazin',
          '/node',
          '/servicelayer/',
          '/preview-app/',
          '/mailings/',
        ],
      },
    },
    abTesting: {
      clientSideEnrollment: {
        activeTestIds: [
          process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging'
            ? '2025_4_Artikelreduktion_test'
            : '2025_4_Artikelreduktion',
        ],
      },
      pRankTestId: 'RE_UniversalAt_TransactionPrank',
    },
    product: productConfig,
    installmentCalculator: installmentCalculatorConfig,
    header: {
      desktopNavigationMode: 'hover',
    },
    order: {
      joe: {
        enabled: true,
      },
      parcelShop: {
        apiKey: 'be22763e-f7e9-4',
        countriesIdentifier: 'AT',
      },
      seals: {
        staatswappen: {
          enabled: true,
          url: 'http://www.staatswappen.at/',
        },
        handelsverband: {
          enabled: true,
          url: 'https://www.handelsverband.at/trustmark-austria/',
        },
        oeonline: {
          enabled: true,
          url: '/service-hilfe/ueber-uns/auszeichnungen/',
        },
      },
      assets: {
        premium: {
          getPremiumImage: ({ device }: { device: string }) =>
            `https://bilder.universal.at/empiriecom/io/universalat/de/BS3_CheckOut_${device}.jpg`,
          getPremiumIcon: () =>
            'https://bilder.universal.at/empiriecom/io/universalat/de/premiumicon_1.svg',
        },
        gifs: {
          success: {
            url: 'https://bilder.universal.at/i/empiriecom/io/de/thank_you_1_transparent.gif',
          },
        },
      },
      hasFlexikontoAdvantageClub: true,
      addressValidation: {
        zipCode: {
          length: 4,
        },
      },
      bankInformation: {
        companyName: 'Universal Versand GmbH',
        bankName: 'Raiffeisenverband Salzburg',
        iban: 'AT26 3500 0000 0304 6323',
        bic: 'RVSAAT2S',
      },
      ...orderConfig,
    },
    footer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    forms: {
      contact: { dynamicHelp },
      newsletterUnsubscribe: {
        useDedicatedSubmitButtonTitleFor: ['unsubscribe|99', 'unsubscribeFlexikonto|98'],
        fields: [
          {
            id: 'newsletter',
            formType: 'radio',
            defaultValue: 'unsubscribe|99',
            options: [
              {
                id: 'frq-daily',
                value: 'frequency|0',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.daily',
              },
              {
                id: 'frq-weekly',
                value: 'frequency|1',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.weekly',
              },
              {
                id: 'unsubscribe',
                value: 'unsubscribe|99',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribe',
              },
              {
                id: 'unsubscribe-flexikonto',
                value: 'unsubscribeFlexikonto|98',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribeFlexikonto',
              },
              {
                id: 'pause',
                value: 'pause|-100',
                messageKey: 'forms.newsletter.unsubscribe.type.pause',
              },
            ],
            additionalForm: {
              id: 'pause',
              formType: 'select',
              type: 'pause',
              showOnlyWhenWhatSelected: 'pause',
              messageKeyPlaceholder: 'forms.newsletter.unsubscribe.type.pause.label',
              defaultValue: '7',
              options: [
                {
                  id: 'pause-7',
                  value: '7',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.7',
                },
                {
                  id: 'pause-14',
                  value: '14',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.14',
                },
                {
                  id: 'pause-21',
                  value: '21',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.21',
                },
                {
                  id: 'pause-30',
                  value: '30',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.30',
                },
              ],
            },
          },
        ],
      },
      newsletterSubscribe: {
        de: {
          unsubscribeLink: 'https://www.universal.at/service-hilfe/newsletter/abmelden/',
          voucherLink: 'https://www.universal.at/service-hilfe/gutscheine-rabatte/',
        },
      },
    },
    banner: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    nlSheetTexts: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/nl-sheet.json`,
      },
    },
    serviceLayer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    seoContent: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/inspire-seofiles/<locale>_${tenantId}-<category>.json`,
      },
      url: {
        'de-AT': {
          privacyNoticePath: '/service-hilfe/ueber-uns/datenschutz',
          potentialActionTarget: 'https://www.universal.at/s/',
        },
      },
    },
    survey: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-surveys/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    promotionBanner: {
      apiEndpoints: {
        bucketEmptyPromotions: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/empty-promotion.json`,
        bucketPromotion: `https://storage.googleapis.com/promotion-data/${tenantId}/<language>/<code>.json`,
        bucketContentSnippet: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/<identifier>.json`,
      },
    },
    voucher: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/promotion-data/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    appBanner: { appUrl: 'https://app.adjust.com/7sq21cg_3c6m099' },
    tracking: {
      clarityIdProduction: 'x8xcsw9e5o',
      categoryIdHomepage: '137398',
      soastaId: 'VCRLH-5E9R4-TT9EZ-675H5-A9TNA',
      tagmanagerId: 'GTM-PZZ54R2',
      googleSiteVerificationID: ['s2QUnwdvTe-7ynyeA5_mieNSFv5fBht-tPHc8U2_BOs'],
      msvalidateID: '4067315BE5669CD6C74BDEC6F6AC5D47',
      pinterestRichPinVerificationID: 'aa7a73f8932f46f1c78af391ae7e3d8e',
      schemaOrgOrganisation: {
        'de-AT': {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': 'https://www.universal.at/',
          name: 'Universal',
          alternateName: 'UNIVERSAL',
          description:
            'Universal ist ein österreichischer Online-Shop, der Möbel, Mode, Schuhe und Technik anbietet. Universal hat sich seit seiner Gründung im Jahr 1957 zu einer traditionsreichen österreichischen Marke entwickelt. Universal ist *nicht* nur ein reiner Online-Händler, sondern ein Anbieter für ein vielfältiges Einkaufserlebnis mit Fokus auf Tradition und Innovation.',
          url: 'https://www.universal.at/',
          logo: 'https://www.universal.at/assets/logos/universalat/logo.svg',
          foundingDate: '1957-01-01',
          founder: {
            '@type': 'Person',
            name: 'Hanns Porst',
            description: 'Gründer von Universal',
            sameAs: 'https://de.wikipedia.org/wiki/Hanns_Porst',
          },
          location: {
            '@type': 'Place',
            name: 'Salzburg',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Peilsteinerstraße 5-7',
              addressLocality: 'Salzburg',
              addressRegion: 'SBG',
              postalCode: '5020',
              addressCountry: 'AT',
            },
          },
          contactPoint: [
            {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              telephone: '+43-662-4485-8',
              email: 'service@universal.at',
              availableLanguage: ['de'],
              hoursAvailable: ['Mo-Su 07:00-22:00'],
              url: 'https://www.universal.at/service-hilfe/kontakt-beratung',
            },
          ],
          sameAs: [
            'https://www.facebook.com/UniversalVersand',
            'https://www.instagram.com/universal.versand/',
            'https://www.pinterest.com/universal1/',
            'https://www.youtube.com/user/UniversalVersand1',
            'https://de.wikipedia.org/wiki/Universal_Versand',
          ],
          legalName: 'Universal Versand GmbH',
          areaServed: 'AT',
          memberOf: {
            '@type': 'Organization',
            name: 'Otto Group',
            sameAs: 'https://www.ottogroup.com/',
          },
          foundingLocation: 'Nürnberg',
          slogan: 'Jeder hat sein Schön...',
        },
      },
    },
    externalScript: {
      kuecheCo: {
        key: 'pAYrueynYRcqna7lGLoVT39kF83Wy3PYENp81Yw0QJqeTyzAjXvc1FwGOzCm',
      },
    },
    headerInformation: {
      de: {
        description:
          'Universal.at - Online Shop für Mode, Möbel, Technik ✓ Gratis 3 Jahre XXL-Garantie ✓ Wunsch-Teilzahlung ✓ Gratis Versand ab EUR 75.- (ausgen. Spedition)',
        title: 'Universal.at | Mode - Möbel - Technik online kaufen',
        canonical: 'https://www.universal.at',
        robots: 'index,follow',
      },
    },
    // kim is renamed as "customerCentricFeedback"
    customerCentricFeedback: {
      tenantId: '1100',
    },
    dynamicYieldTracking: {
      key: 'bedfbdea4d3d014a8b838c361b7e67a5e683f21ac62f094f2dd353b478251146',
    },
    geoCountryLayer: {
      enabled: true,
      flag: 'at',
      alternatives: [
        {
          flag: 'de',
          shops: [{ link: 'https://www.baur.de', title: 'baur.de' }],
        },
        {
          flag: 'ch',
          shops: [{ link: 'https://www.ackermann.ch', title: 'ackermann.ch' }],
        },
      ],
    },
    chatBot: {
      enabled: true,
      key: 'bae76f5c925d4dd6b37748fd6b9c01010a2cd46fafd94b56813753bb4fd6b644',
      includePaths: ['/', '/service-hilfe'],
      excludePaths: [
        '/service-hilfe/newsletter/anmelden',
        '/service-hilfe/newsletter/abmelden',
        '/service-hilfe/nachhaltigkeit',
        '/service-hilfe/ueber-uns',
      ],
    },
    speedKit: {
      enabled: true,
      scriptUrl: 'https://ottoaustria.app.baqend.com/v1/speedkit/install.js',
    },
    ...paymentMethodsConfig,
  },
  { arrayMerge: customArrayMerge },
);

export const getFullLocale = (localeOrLanguage: string | undefined) =>
  getFullLocaleFromConfig(localeOrLanguage, config);

// eslint-disable-next-line import-x/no-default-export
export default config;
